import { type VFC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { ActionButton } from "widgets";

import { type Role } from "../../graphql.js";
import { loadAllCapabilities, loadRoles } from "../../redux/modules/roles-admin.js";
import { loadAllSessions } from "../../redux/modules/users-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import { ModalActionWidget } from "../core/overlays/index.js";
import EditRoleDialog from "./EditRoleDialog.js";
import { RoleAdminPage } from "./RoleAdminPage.js";

/**
 * Roles Admin Page main component
 */
const RolesAdminPage: VFC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();

  const [isLoadingRoles, setLoadingRoles] = useState(false);
  const [isLoadingAssignments, setLoadingAssignments] = useState(false);
  const roles = useSelector<IAppState, Role[]>((state) => state.rolesAdmin.roles);

  const loadAssignments = useCallback(() => {
    setLoadingAssignments(true);
    Promise.all([dispatch(loadAllSessions()), dispatch(loadAllCapabilities())])
      .then(() => setLoadingAssignments(false))
      .catch(() => setLoadingAssignments(false));
  }, [dispatch]);

  const loadAllRoles = useCallback(() => {
    setLoadingRoles(true);
    dispatch(loadRoles())
      .then(() => setLoadingRoles(false))
      .catch(() => setLoadingRoles(false));
  }, [dispatch]);

  useEffect(() => {
    loadAllRoles();
    loadAssignments();
  }, [loadAllRoles, loadAssignments]);

  const getNavPanelCategories = (): INavPanelCategory[] => {
    return [
      {
        label: "Roles",
        widget: (
          <ModalActionWidget
            key={"edit-role-button"}
            actionWidget={({ open }) => (
              <ActionButton label={"New Role"} onPress={() => open()} icon={"add"} style={{ marginBottom: 10 }} />
            )}
          >
            {({ close }) => (
              <EditRoleDialog
                role={{
                  id: null,
                  name: null,
                  description: null,
                  capabilities: [],
                  users: [],
                  legacy: false,
                }}
                titleMessage={"New Role"}
                buttonLabel={"Save"}
                onSelect={(role) => {
                  dispatch(loadRoles()).then(() => navigate(`/admin/roles/${role.id}`));
                  close();
                }}
                onCancel={() => close()}
              />
            )}
          </ModalActionWidget>
        ),
        navItems: roles?.map((role: Role, index) => {
          return {
            label: role.name,
            route: `${role.id}`,
            //matchingRoute: "/:selectedRole",
            disabled: isLoadingRoles,
            default: index === 0,
            pageComponent: <RoleAdminPage isLoading={isLoadingAssignments} />,
          };
        }),
      },
    ];
  };

  return <PageLayout navPanelCategories={getNavPanelCategories()} />;
};

export default RolesAdminPage;
